import { humanizePeriodUnitDays, humanizePeriodUnitMonths } from "frontend/order_tunnel/utils/index";


/**
 * Given a billing frequency in months, will generate the text that describes how often the user will be charged.
 *
 * E.g.
 *  generateBillingFrequencyText(1).text === 'per month'
 *  generateBillingFrequencyText(3).text === 'every 3 months'
 *
 * @param billingFrequency - How often the user will be billed given in months.
 * @returns {{durationNumber: *, text: string, durationUnit: *}}
 */
export function generateBillingFrequencyText(billingFrequency) {
    const [durationNumber, durationUnit] = humanizePeriodUnitMonths(billingFrequency);
    if (durationNumber > 1) {
        return {
            text: `every ${durationNumber} ${durationUnit}`,
            durationNumber,
            durationUnit,
        };
    }
    return {
        text: `per ${durationUnit}`,
        durationNumber,
        durationUnit,
    };
}

/**
 * Generate the correct pricing period breakdown for the upfront cost shown to the user.
 *
 * If there is a trial then the trial information will be used to generate the message.
 *
 * If there are discounts then we generate a message based on the discount with the shortest period.
 *
 * If there are no trials or discounts then we generate the period text for the base billing frequency of the plan.
 *
 * @param product - The product_listing object passed from the OrderTunnelPage `product_listings` array.
 * @returns {string}
 */
export function generatePricePeriodText(product) {
    // Show breakdown of the trial
    if (product.braintree.trial_period) {
        let humanizeHelper = humanizePeriodUnitMonths;
        if (product.braintree.trial_duration_unit === 'day') {
            humanizeHelper = humanizePeriodUnitDays;
        }
        const [duration_number, duration_unit] = humanizeHelper(product.braintree.trial_duration);
        return `for ${duration_number} ${duration_unit}`;
    }
    // Show breakdown of the discount applied
    if (product.braintree.discounts && product.braintree.discounts.length > 0) {
        const discountDurations = [];
        product.braintree.discounts.forEach(
            (discount) => {
                if (discount.number_of_billing_cycles !== null) {
                    discountDurations.push(discount.number_of_billing_cycles);
                }
            }
        );

        const {
            text: billingFrequencyText,
            durationNumber: billingFreqDurationNumber,
            durationUnit: billingFreqDurationUnit,
        } = generateBillingFrequencyText(product.braintree.billing_frequency);

        // If there are no temporary discounts then display the normal billing frequency text
        // e.g. "per year"
        if (discountDurations.length === 0) {
            return billingFrequencyText;
        }

        // Find the period of time when all of the discounts will be active
        const shortestDiscountDuration = Math.min(...discountDurations);

        const [discountDurationNumber, discountDurationUnit] = humanizePeriodUnitMonths(
            product.braintree.billing_frequency * shortestDiscountDuration
        );

        const discountPeriodText = `for ${discountDurationNumber} ${discountDurationUnit}`;

        // If the billing frequency is the same as the duration of the discount then we can just give the discount
        // description. This avoids saying things like: `every 3 months for 3 months` and will instead be `for 3 months`
        if (discountDurationUnit === billingFreqDurationUnit && discountDurationNumber === billingFreqDurationNumber) {
            return discountPeriodText;
        }
        return `${billingFrequencyText} ${discountPeriodText}`;
    }

    const {text: billingText} = generateBillingFrequencyText(product.braintree.billing_frequency);
    // No discount or trial so display breakdown of the base price
    return billingText;
}


/**
 * Generate the text explaining what the normal rate for the product is after all discounts/trials have ended.
 *
 * If there are no trials or discounts will return a blank string.
 *
 * If there are discounts on the plan that do not expire, then the discounted price will be used as the normal price.
 *
 * @param product - The product_listing object passed from the OrderTunnelPage `product_listings` array.
 * @returns {string}
 */
export function generateNormalPricingText(product) {
    const hasDiscounts = product.braintree.discounts && product.braintree.discounts.length > 0;
    const hasTrial = product.braintree.trial_period;
    if (!hasDiscounts && !hasTrial) {
        return "";
    }

    if (product.braintree.number_of_billing_cycles === 1)
        return "";

    let normalPrice = Number(product.braintree.price);
    if (hasDiscounts) {
        product.braintree.discounts.map((discount) => {
            if (discount.never_expires) {
                const discountedPrice = normalPrice - discount.amount;
                normalPrice = Number(discountedPrice.toFixed(2));
            }
        });
    }

    // Don't need to display a special normal rate information if the normal rate is the same as the rate they
    // are paying initially
    if (normalPrice === Number(product.braintree.upfront_cost)) {
        return "";
    }

    const normalPriceAmount = normalPrice.toFixed(2);

    const isAutoRenewing = product.braintree.number_of_billing_cycles === null;
    const [billingPeriodNumber, billingPeriodUnit] = humanizePeriodUnitMonths(product.braintree.billing_frequency);

    const baseNormalRateText = `then $${normalPriceAmount} /`;
    let normalRateText;
    if (billingPeriodNumber > 1) {
        normalRateText = `${baseNormalRateText} ${billingPeriodNumber} ${billingPeriodUnit}`;
    } else {
        normalRateText = `${baseNormalRateText} ${billingPeriodUnit}`;
    }

    if (isAutoRenewing) {
        return normalRateText;
    }

    // The number of months the subscription will be active.
    const totalSubscriptionDuration = product.braintree.number_of_billing_cycles * product.braintree.billing_frequency;
    const [subscriptionDurationNumber, subscriptionDurationUnit] = humanizePeriodUnitMonths(totalSubscriptionDuration);

    return `${normalRateText} for ${subscriptionDurationNumber} ${subscriptionDurationUnit}`
}